import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getData, postData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";

const SEVERITY_CONFIG = {
  ok:       { color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0", icon: "\u2705", label: "Verified" },
  info:     { color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE", icon: "\u2139\uFE0F",  label: "Info" },
  warning:  { color: "#D97706", bg: "#FFF7ED", border: "#FED7AA", icon: "\u26A0\uFE0F",  label: "Warning" },
  blocking: { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", icon: "\uD83D\uDED1", label: "Blocking" },
  pending:  { color: "#64748B", bg: "#F8FAFC", border: "#E2E8F0", icon: "\u23F3", label: "Pending" },
  failed:   { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", icon: "\u274C", label: "Failed" },
};

/**
 * T2TVerificationPanel \u2014 Shows verification badges for a vendor request.
 * Displayed on Finance and Approver stages.
 *
 * Props:
 *  - requestId: string/number
 *  - vendorType: "domestic" | "international"
 *  - stage: "finance" | "approver"
 */
const T2TVerificationPanel = ({ requestId, vendorType = "domestic", stage = "finance" }) => {
  const toast = useToast();
  const [verifications, setVerifications] = useState([]);
  const [mismatches, setMismatches] = useState([]);
  const [dedupResults, setDedupResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!requestId) return;
    const fetchAll = async () => {
      setLoading(true);
      const [verifResp, mismatchResp, dedupResp] = await Promise.all([
        getData(`t2t/requests/${requestId}/verifications`),
        getData(`t2t/vendors/${requestId}/mismatches`),
        getData(`t2t/vendors/${requestId}/dedup-results`),
      ]);
      if (!verifResp.error) {
        const raw = verifResp.data?.data;
        setVerifications(Array.isArray(raw) ? raw : []);
      }
      if (!mismatchResp.error) {
        const raw = mismatchResp.data?.data;
        setMismatches(Array.isArray(raw) ? raw : []);
      }
      if (!dedupResp.error) {
        const raw = dedupResp.data?.data;
        setDedupResults(Array.isArray(raw) ? raw : []);
      }
      setLoading(false);
    };
    fetchAll();
  }, [requestId]);

  const resolveMismatch = async (mismatchId, resolution) => {
    const resp = await postData(`t2t/vendors/${requestId}/mismatches/${mismatchId}/resolve`, { resolution });
    if (resp.error) { toast(resp.error.message || "Failed to resolve.", "error"); return; }
    setMismatches((prev) => prev.map((m) => m.id === mismatchId ? { ...m, resolution } : m));
    toast("Mismatch resolved.", "success");
  };

  if (loading) return <div style={{ padding: 16, fontSize: 12, color: "var(--t2t-text-muted)" }}>Loading verifications...</div>;

  const domesticChecks = [
    { key: "pan", label: "PAN Verification", field: "pan_number" },
    { key: "gst", label: "GST Verification", field: "gstin" },
    { key: "bank", label: "Bank Account Verification", field: "bank_account_number" },
  ];
  const intlChecks = [
    { key: "vat", label: "VAT / Tax ID Verification", field: "vat_number" },
    { key: "swift", label: "SWIFT / BIC Verification", field: "swift_bic" },
  ];
  const checks = vendorType === "domestic" ? domesticChecks : intlChecks;

  return (
    <div className="t2t-verification-panel">
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Verification Status</div>

      {/* Verification badges */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {checks.map((check) => {
          const v = verifications.find((vr) => vr.verification_type === check.key || vr.field_name === check.field);
          const status = v ? (v.status === "verified" || v.status === "ok" ? "ok" : v.status === "failed" ? "failed" : v.status === "mismatch" ? "warning" : "pending") : "pending";
          const cfg = SEVERITY_CONFIG[status];
          const isExpanded = expandedId === check.key;

          return (
            <div key={check.key} style={{ border: `1px solid ${cfg.border}`, borderRadius: 8, overflow: "hidden" }}>
              <div
                onClick={() => setExpandedId(isExpanded ? null : check.key)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: cfg.bg, cursor: "pointer" }}
              >
                <span style={{ fontSize: 16 }}>{cfg.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{check.label}</div>
                  <div style={{ fontSize: 11, color: cfg.color, fontWeight: 600 }}>{cfg.label}</div>
                </div>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>{isExpanded ? "\u25B2" : "\u25BC"}</span>
              </div>

              {isExpanded && v && (
                <div style={{ padding: "10px 14px", fontSize: 12, borderTop: `1px solid ${cfg.border}` }}>
                  {v.holder_name && <div><strong>Holder:</strong> {v.holder_name}</div>}
                  {v.pan_type && <div><strong>PAN Type:</strong> {v.pan_type}</div>}
                  {v.gst_status && <div><strong>GST Status:</strong> {v.gst_status}</div>}
                  {v.gst_state && <div><strong>GST State:</strong> {v.gst_state}</div>}
                  {v.bank_name_returned && <div><strong>Bank Name (API):</strong> {v.bank_name_returned}</div>}
                  {v.aadhaar_linked !== undefined && <div><strong>Aadhaar Linked:</strong> {v.aadhaar_linked ? "Yes" : "No"}</div>}
                  {v.swift_valid !== undefined && <div><strong>SWIFT Valid:</strong> {v.swift_valid ? "Yes" : "No"}</div>}
                  {v.verified_at && <div style={{ marginTop: 4, color: "var(--t2t-text-muted)" }}>Verified at: {new Date(v.verified_at).toLocaleString()}</div>}
                  {v.error_message && <div style={{ color: "#DC2626", marginTop: 4 }}><strong>Error:</strong> {v.error_message}</div>}
                </div>
              )}

              {isExpanded && !v && (
                <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--t2t-text-muted)" }}>
                  Verification not yet run. It will execute automatically after form submission.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mismatches */}
      {mismatches.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Data Mismatches</div>
          {mismatches.map((m) => {
            const sev = SEVERITY_CONFIG[m.severity] || SEVERITY_CONFIG.warning;
            const resolved = m.resolution && m.resolution !== "pending";
            return (
              <div key={m.id} style={{ border: `1px solid ${sev.border}`, borderRadius: 8, padding: 12, marginBottom: 8, background: resolved ? "#F8FAFC" : sev.bg, opacity: resolved ? 0.7 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 14 }}>{sev.icon}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: sev.color, textTransform: "uppercase" }}>{m.severity}</span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{m.field_name}</span>
                  {resolved && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "#E2E8F0", color: "#475569", fontWeight: 600 }}>{m.resolution}</span>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginBottom: 2 }}>Submitted Value</div>
                    <div style={{ fontWeight: 600, padding: "4px 8px", background: "white", borderRadius: 4, border: "1px solid #E2E8F0" }}>{m.submitted_value}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: "var(--t2t-text-muted)", marginBottom: 2 }}>Verified Value (API)</div>
                    <div style={{ fontWeight: 600, padding: "4px 8px", background: "white", borderRadius: 4, border: "1px solid #E2E8F0" }}>{m.verified_value}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 6 }}>Match score: {m.match_score}%</div>

                {!resolved && stage === "finance" && (
                  <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                    <button className="t2t-btn t2t-btn-primary t2t-btn-sm" style={{ fontSize: 10 }} onClick={() => resolveMismatch(m.id, "accepted")}>Accept</button>
                    <button className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ fontSize: 10 }} onClick={() => resolveMismatch(m.id, "corrected")}>Correct Data</button>
                    <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 10, color: "#DC2626" }} onClick={() => resolveMismatch(m.id, "escalated")}>Escalate</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Deduplication Results */}
      {dedupResults.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Deduplication Check</div>
          {dedupResults.map((d) => {
            const isHigh = d.match_score >= 75;
            const resolved = d.resolution && d.resolution !== "pending";
            return (
              <div key={d.id} style={{
                border: `2px solid ${isHigh ? "#DC2626" : "#D97706"}`, borderRadius: 8, padding: 14, marginBottom: 8,
                background: resolved ? "#F8FAFC" : isHigh ? "#FEF2F2" : "#FFF7ED",
                opacity: resolved ? 0.7 : 1,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16 }}>{isHigh ? "\uD83D\uDD34" : "\uD83D\uDFE1"}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: isHigh ? "#DC2626" : "#D97706" }}>
                    {d.match_score}% match with existing vendor
                  </span>
                  {resolved && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "#E2E8F0", color: "#475569", fontWeight: 600 }}>{d.resolution}</span>}
                </div>

                {d.matched_vendor_number && (
                  <div style={{ fontSize: 12, marginBottom: 8 }}>
                    Matched: <strong>{d.matched_vendor_number}</strong> ({d.matched_vendor_name || "..."})
                  </div>
                )}

                {/* Field-level match breakdown */}
                {d.match_fields && typeof d.match_fields === "object" && (
                  <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                    {Object.entries(d.match_fields).map(([field, score]) => (
                      <div key={field} style={{ fontSize: 11 }}>
                        <span style={{ color: "var(--t2t-text-muted)" }}>{field}: </span>
                        <span style={{ fontWeight: 700, color: score >= 90 ? "#DC2626" : score >= 70 ? "#D97706" : "#16A34A" }}>{score}%</span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 8 }}>
                  Type: {d.match_type} | Checked: {d.checked_at ? new Date(d.checked_at).toLocaleString() : "..."}
                </div>

                {!resolved && stage === "approver" && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 10, color: "#DC2626", borderColor: "#DC2626" }}
                      onClick={async () => {
                        const resp = await postData(`t2t/vendors/${requestId}/dedup-resolve`, { dedup_id: d.id, resolution: "confirmed_duplicate" });
                        if (!resp.error) { setDedupResults((prev) => prev.map((dd) => dd.id === d.id ? { ...dd, resolution: "confirmed_duplicate" } : dd)); toast("Marked as duplicate.", "success"); }
                      }}>Confirm Duplicate \u2014 Reject</button>
                    <button className="t2t-btn t2t-btn-primary t2t-btn-sm" style={{ fontSize: 10 }}
                      onClick={async () => {
                        const note = prompt("Explain why these are different vendors despite high similarity:");
                        if (!note || note.trim().length < 5) { toast("Explanation is required (min 5 chars).", "warning"); return; }
                        const resp = await postData(`t2t/vendors/${requestId}/dedup-resolve`, { dedup_id: d.id, resolution: "confirmed_different", note });
                        if (!resp.error) { setDedupResults((prev) => prev.map((dd) => dd.id === d.id ? { ...dd, resolution: "confirmed_different" } : dd)); toast("Marked as different vendors.", "success"); }
                      }}>These Are Different \u2014 Approve</button>
                    <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ fontSize: 10 }}
                      onClick={async () => {
                        const resp = await postData(`t2t/vendors/${requestId}/dedup-resolve`, { dedup_id: d.id, resolution: "escalated" });
                        if (!resp.error) { setDedupResults((prev) => prev.map((dd) => dd.id === d.id ? { ...dd, resolution: "escalated" } : dd)); toast("Escalated to admin.", "success"); }
                      }}>Escalate</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* All clear message */}
      {verifications.length > 0 && mismatches.length === 0 && dedupResults.length === 0 && (
        <div style={{ padding: 12, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, textAlign: "center", fontSize: 12, color: "#166534" }}>
          All verifications passed. No mismatches or duplicates detected.
        </div>
      )}
    </div>
  );
};

T2TVerificationPanel.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  vendorType: PropTypes.oneOf(["domestic", "international"]),
  stage: PropTypes.oneOf(["finance", "approver"]),
};

export default T2TVerificationPanel;
